import { formatTime } from "@clipboard-health/date-time";
import { Box, CircularProgress, DialogContent, Stack } from "@mui/material";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { OpenShiftCardContainer } from "@src/appV2/redesign/Shift/Open/CardContainer";
import { type RateNegotiation } from "@src/appV2/redesign/Shift/Open/types";
import { OpenShiftListDataProvider } from "@src/appV2/redesign/Shift/Open/useOpenShiftListDataContext";
import { SHIFT_DISCOVERY_SHIFT_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { CbhIcon, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isValid, parseISO } from "date-fns";
import { useEffect, useMemo, useRef } from "react";

import { Button } from "../../components/Button";
import { useGetFcfReplacementShifts } from "./hooks/useGetFcfReplacementShifts";
import { type FcfDeepLinkParams, type FcfDismissMethod, type FcfModalSource } from "./types";

const EMPTY_RATE_NEGOTIATIONS_MAP = new Map<string, RateNegotiation>();

interface FcfReplacementShiftsBottomSheetProps {
  modalState: UseModalState;
  onDismiss: () => void;
  onInteract?: () => Promise<void>;
  onNoEligibleShifts?: () => void;
  onRequestDismissLog?: () => boolean;
  params: FcfDeepLinkParams;
  source: FcfModalSource;
}

export function FcfReplacementShiftsBottomSheet(props: FcfReplacementShiftsBottomSheetProps) {
  const {
    modalState,
    onDismiss,
    onInteract,
    onNoEligibleShifts,
    onRequestDismissLog,
    params,
    source,
  } = props;
  const { navigateToModal } = useShiftModalsDataContext();
  const { userId: workerId } = useDefinedWorker();

  const { replacementShifts, isLoading, isSuccess } = useGetFcfReplacementShifts({
    ...params,
    enabled: modalState.modalIsOpen,
  });

  const { data: cancelledWorkplaceData, isLoading: isWorkplaceLoading } = useGetWorkplace({
    workplaceId: params.cancelledWorkplaceId,
  });
  const cancelledWorkplaceName = cancelledWorkplaceData?.data.attributes.name;
  const parsedShiftStart = parseISO(params.shiftStart);
  const formattedShiftStart = isValid(parsedShiftStart) ? formatTime(parsedShiftStart) : undefined;

  const isAnyLoading = isLoading || isWorkplaceLoading;
  const isEmpty = isSuccess && replacementShifts.length === 0;

  const hasLoggedViewRef = useRef(false);

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      hasLoggedViewRef.current = false;
      return;
    }
    if (!isSuccess || hasLoggedViewRef.current) {
      return;
    }
    hasLoggedViewRef.current = true;
    logEvent(APP_V2_USER_EVENTS.FCF_REPLACEMENT_MODAL_VIEWED, {
      cancellation_id: params.cancellationId,
      worker_id: workerId,
      source,
      viewed_at: new Date().toISOString(),
      shifts_shown_count: replacementShifts.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.modalIsOpen, isSuccess]);

  useEffect(() => {
    if (isEmpty) {
      onNoEligibleShifts?.();
    }
    // onNoEligibleShifts is intentionally excluded: the callback identity is not
    // stable across renders, but we only want to fire once when isEmpty first
    // becomes true, not every time the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty]);

  const workplacesMap = useMemo(
    () => new Map(replacementShifts.map(({ workplace }) => [workplace.id, workplace])),
    [replacementShifts]
  );

  function logDismiss(dismissMethod: FcfDismissMethod): void {
    if (onRequestDismissLog?.() === false) {
      return;
    }
    logEvent(APP_V2_USER_EVENTS.FCF_REPLACEMENT_MODAL_DISMISSED, {
      cancellation_id: params.cancellationId,
      worker_id: workerId,
      dismiss_method: dismissMethod,
      dismissed_at: new Date().toISOString(),
      source,
    });
  }

  function handleShiftTap(shiftId: string): void {
    logEvent(APP_V2_USER_EVENTS.FCF_REPLACEMENT_MODAL_SHIFT_TAPPED, {
      cancellation_id: params.cancellationId,
      worker_id: workerId,
      tapped_shift_id: shiftId,
      tapped_at: new Date().toISOString(),
    });
    void onInteract?.();
    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
      clearExistingSearchParams: true,
      pathParams: { shiftId },
    });
  }

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <Box sx={{ display: "flex", justifyContent: "flex-end", paddingX: 5, marginTop: 4 }}>
        <IconButton
          iconType="close"
          aria-label="Close"
          variant="outlined"
          size="medium"
          onClick={() => {
            logDismiss("close_button_tap");
            onDismiss();
          }}
        />
      </Box>
      {!isAnyLoading && !isEmpty && (
        <Stack spacing={2} sx={{ paddingX: 6, marginTop: 4, marginBottom: 4 }}>
          <Text
            textAlign="center"
            variant="body1"
            sx={{ color: (theme) => theme.palette.text.secondary }}
          >
            {cancelledWorkplaceName !== undefined && formattedShiftStart !== undefined
              ? `We're sorry your ${formattedShiftStart} shift at ${cancelledWorkplaceName} was cancelled`
              : `We're sorry your shift was cancelled`}
          </Text>
          <Text
            textAlign="center"
            variant="h4"
            sx={{ color: (theme) => theme.palette.text?.primary }}
          >
            Here are some nearby shifts starting at the same time
          </Text>
        </Stack>
      )}
      <DialogContent>
        {isAnyLoading && (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              paddingY: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isEmpty && (
          <Stack alignItems="center" spacing={8} height="100%" justifyContent="center">
            <CbhIcon size="xLarge" type="search" />
            <Text
              textAlign="center"
              variant="body1"
              sx={{ color: (theme) => theme.palette.text?.secondary ?? "" }}
            >
              Looks like all replacement shifts have since been booked, sorry about that!
            </Text>
          </Stack>
        )}

        {!isAnyLoading && !isEmpty && (
          <OpenShiftListDataProvider
            openShifts={replacementShifts.map(({ shift }) => shift)}
            workplacesMap={workplacesMap}
            rateNegotiationsMap={EMPTY_RATE_NEGOTIATIONS_MAP}
          >
            <Stack spacing={3}>
              {replacementShifts.map(({ shift, workplace }) => (
                <OpenShiftCardContainer
                  key={shift.id}
                  shift={shift}
                  workplace={workplace}
                  workers={[]}
                  onClick={() => {
                    handleShiftTap(shift.id);
                  }}
                />
              ))}
            </Stack>
          </OpenShiftListDataProvider>
        )}
      </DialogContent>

      <DialogFooter withShadow={replacementShifts.length >= 3} orientation="horizontal">
        <Button
          fullWidth
          size="large"
          variant="outlinedHeavy"
          onClick={() => {
            logDismiss("not_interested_tap");
            onDismiss();
          }}
        >
          {isEmpty ? "Okay" : "I'm not interested"}
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
